import {
    cloneElement,
    isValidElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type MutableRefObject,
    type ReactNode
} from "react";

import { css } from "@compiled/react";
import { flattenValues } from "@uniformdev/canvas";
import {
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";
import { defineMessages, useIntl } from "react-intl";
import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";
import { SortDefinition } from "src/uniform/contenttypes/sortDefinition";
import { FilterableCardItemDataContext } from "src/uniform/contexts/filterable-card-item-data-context";
import { FilterableGalleryProvider } from "src/uniform/contexts/filterable-gallery-context";
import {
    FilterableGalleryLoaderProvider,
    useFilterableGalleryLoader,
    useIsInsideLoaderProvider
} from "src/uniform/contexts/filterable-gallery-loader-context";
import {
    FilterableItemIndexContext,
    FilterableItemRegistryProvider
} from "src/uniform/contexts/filterable-item-registry-context";

import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Adg4Skeleton } from "@/components/adg4/skeleton/src/skeleton";
import {
    ColumnLayout,
    ColumnLayoutProps,
    wacToken
} from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

import { subscribeToShowAllFilterableGalleryItems } from "../../filterable-gallery-display-limit-override";

import { FILTER_DROPDOWN_DATA_ATTR } from "./components/filter-control";
import { Toolbar } from "./components/toolbar";
import { useFilterState } from "./hooks/use-filter-state";
import { useOutsideClick } from "./hooks/use-outside-click";
import {
    applyFilters,
    applySearch,
    applySort,
    buildLabelMaps
} from "./utils/filterEngine";

type UniformValueParameter<T> = T | { value?: T };

type UniformBlockParameter = unknown;

type GalleryDataItem = unknown;

interface FacetDataSourceEntry {
    fields?: {
        sourceId?: { value?: unknown };
        data?: { value?: unknown };
    };
}

const messages = defineMessages({
    emptyState: {
        id: "filterable-gallery.empty-state",
        defaultMessage: "No results found."
    }
});

export interface UniformFilterableGalleryProps {
    readonly searchFieldPaths?: string[];
    readonly showSearchInput?: UniformValueParameter<boolean>;
    readonly displayLimit?: UniformValueParameter<number | string>;
    readonly filters?: UniformBlockParameter;
    readonly sorts?: UniformBlockParameter;
    readonly urlSync?: boolean;
    readonly layout?: ColumnLayoutProps["layout"];
    readonly facetDataSources?: UniformBlockParameter;
    readonly pageSize?: UniformValueParameter<number | string>;
    readonly data?: GalleryDataItem[];
    readonly featuredItemSlugs?: UniformValueParameter<string[]>;
    readonly loadMoreLabel?: string;
    readonly emptyStateLabel?: string;
}

interface FilterableGallerySlotProps extends UniformSlotWrapperComponentProps {
    itemDataArrayRef: MutableRefObject<GalleryDataItem[]>;
    rawSearchFieldPaths?: string[];
    showSearchInput?: UniformValueParameter<boolean>;
    displayLimit?: UniformValueParameter<number | string>;
    rawFilters?: UniformBlockParameter;
    rawSorts?: UniformBlockParameter;
    urlSync: boolean;
    layout: ColumnLayoutProps["layout"];
    rawFacetDataSources?: UniformBlockParameter;
    pageSize?: number;
    dataItems?: GalleryDataItem[];
    featuredItemSlugs: string[];
    loadMoreLabel?: string;
    emptyStateLabel?: string;
}

const FilterableGallerySlot = ({
    items,
    itemDataArrayRef,
    rawSearchFieldPaths,
    showSearchInput = true,
    displayLimit: rawDisplayLimit,
    rawFilters,
    rawSorts,
    urlSync,
    layout,
    rawFacetDataSources,
    pageSize,
    dataItems,
    featuredItemSlugs,
    loadMoreLabel,
    emptyStateLabel
}: FilterableGallerySlotProps) => {
    const [displayLimitOverrideQuery, setDisplayLimitOverrideQuery] = useState<
        string | undefined
    >();
    const { extraItems, isAwaitingBackfill } = useFilterableBackfillState();
    const [visibleCount, setVisibleCount] = useState<number>(() =>
        resolveInitialVisibleCount(pageSize, dataItems?.length)
    );
    const registeredItems = itemDataArrayRef.current;

    const itemDataArray = useMemo(() => {
        if (dataItems && dataItems.length > 0) {
            const seenIds = new Set<string>();
            return [...dataItems, ...extraItems].filter((item) => {
                const id = getSysId(item);
                if (!id) return true;
                if (seenIds.has(id)) return false;
                seenIds.add(id);
                return true;
            });
        }
        if (extraItems.length === 0) return registeredItems;
        const seenIds = new Set<string>();
        for (const item of registeredItems) {
            const id = getSysId(item);
            if (id) seenIds.add(id);
        }
        const newExtras = extraItems.filter((item) => {
            const id = getSysId(item);
            return id ? !seenIds.has(id) : true;
        });
        return [...registeredItems, ...newExtras];
    }, [registeredItems, extraItems, dataItems]);

    const searchFieldPaths = useMemo(
        () =>
            Array.isArray(rawSearchFieldPaths)
                ? rawSearchFieldPaths.filter(
                      (path) => typeof path === "string" && path
                  )
                : [],
        [rawSearchFieldPaths]
    );

    const shouldShowSearchInput = resolveBooleanParameter(
        showSearchInput,
        true
    );
    const displayLimit = resolvePositiveIntegerParameter(rawDisplayLimit);
    const skeletonCardCount = resolveSkeletonCardCount(
        displayLimit,
        dataItems?.length ?? items.length
    );

    const filterDefs = useMemo<FilterDefinition[]>(() => {
        if (!rawFilters) return [];
        const flat = flattenValues(rawFilters) as unknown as FilterDefinition[];
        return Array.isArray(flat) ? flat : [];
    }, [rawFilters]);

    const sortDefs = useMemo<SortDefinition[]>(() => {
        if (!rawSorts) return [];
        const flat = flattenValues(rawSorts) as unknown as SortDefinition[];
        return Array.isArray(flat) ? flat : [];
    }, [rawSorts]);

    const optionsDataSources = useMemo<Record<string, unknown[]>>(() => {
        if (!rawFacetDataSources) return {};
        const rawEntries = getUniformBlockEntries(rawFacetDataSources);
        if (rawEntries.length === 0) return {};

        return Object.fromEntries(
            rawEntries.flatMap((entry) => {
                const sourceId = entry.fields?.sourceId?.value;
                const data = entry.fields?.data?.value;
                if (typeof sourceId !== "string" || !Array.isArray(data)) {
                    return [];
                }
                return [[sourceId, data]];
            })
        );
    }, [rawFacetDataSources]);

    const {
        filterObject,
        searchQuery,
        selectedSortId,
        dropdownVisibility,
        isAnyDropdownOpen,
        setSearchQuery,
        handleFilterButtonClick,
        toggleFilterOption,
        clearAllFilters,
        handleSearchSubmit,
        handleSearchClear,
        handleSortChange,
        closeAllDropdownsAndCommit
    } = useFilterState({
        items: itemDataArray,
        filterDefs,
        urlSync,
        optionsDataSources
    });

    const showAllSearchResults = useCallback((query: string) => {
        const trimmedQuery = query.trim();
        setDisplayLimitOverrideQuery(trimmedQuery);
        setVisibleCount(Infinity);
    }, []);

    const handleSearchSubmitAndShowAll = useCallback(() => {
        handleSearchSubmit();
        showAllSearchResults(searchQuery);
    }, [handleSearchSubmit, searchQuery, showAllSearchResults]);

    useEffect(
        () =>
            subscribeToShowAllFilterableGalleryItems(({ query }) => {
                setSearchQuery(query);
                showAllSearchResults(query);
            }),
        [setSearchQuery, showAllSearchResults]
    );

    const trimmedSearchQuery = searchQuery.trim();
    const shouldShowAllSearchResults =
        trimmedSearchQuery.length > 0 &&
        displayLimitOverrideQuery === trimmedSearchQuery;

    useEffect(() => {
        if (shouldShowAllSearchResults) {
            setVisibleCount(Infinity);
            return;
        }
        if (pageSize !== undefined) {
            setVisibleCount(pageSize);
            return;
        }
        if (dataItems !== undefined) {
            setVisibleCount(dataItems.length);
        }
    }, [
        filterObject,
        searchQuery,
        pageSize,
        dataItems,
        shouldShowAllSearchResults
    ]);

    const sortedMatchingDataItems = useMemo(() => {
        const matchingDataItems = applySearch(
            applyFilters(itemDataArray, filterDefs, filterObject),
            searchFieldPaths,
            searchQuery
        );
        const selectedSort = sortDefs.find(
            (sort) => sort.id === selectedSortId
        );
        if (selectedSort) {
            return applySort(matchingDataItems, selectedSort);
        }
        if (trimmedSearchQuery.length > 0) {
            return matchingDataItems;
        }
        return prioritizeItemsBySlug(matchingDataItems, featuredItemSlugs);
    }, [
        itemDataArray,
        filterDefs,
        filterObject,
        searchFieldPaths,
        searchQuery,
        sortDefs,
        selectedSortId,
        trimmedSearchQuery,
        featuredItemSlugs
    ]);

    const shouldApplyDisplayLimit = getShouldApplyDisplayLimit(
        displayLimit,
        shouldShowAllSearchResults
    );

    const usesVisibleCount = getUsesVisibleCount(pageSize, dataItems);
    const effectiveVisibleCount = usesVisibleCount ? visibleCount : undefined;

    const visibleDataItems = useMemo(
        () =>
            effectiveVisibleCount !== undefined
                ? sortedMatchingDataItems.slice(0, effectiveVisibleCount)
                : shouldApplyDisplayLimit
                ? sortedMatchingDataItems.slice(0, displayLimit)
                : sortedMatchingDataItems,
        [
            effectiveVisibleCount,
            displayLimit,
            shouldApplyDisplayLimit,
            sortedMatchingDataItems
        ]
    );

    const hasMoreToShow =
        effectiveVisibleCount !== undefined &&
        visibleCount < sortedMatchingDataItems.length;

    const visibleIndices = useMemo(
        () => getOrderedIndicesForDataItems(itemDataArray, visibleDataItems),
        [itemDataArray, visibleDataItems]
    );

    const visibleIndexSet = useMemo(
        () => new Set(visibleIndices),
        [visibleIndices]
    );

    const labelMaps = useMemo(
        () => buildLabelMaps(itemDataArray, filterDefs, optionsDataSources),
        [itemDataArray, filterDefs, optionsDataSources]
    );

    useOutsideClick({
        isActive: isAnyDropdownOpen,
        insideMarkerAttribute: FILTER_DROPDOWN_DATA_ATTR,
        onOutsideClick: closeAllDropdownsAndCommit
    });

    const isRegistered = itemDataArray.length > 0;

    const allSlotItems = useMemo(() => {
        const slotChildren = items as ReactNode[];

        const cardTemplate = slotChildren.find(isValidElement);
        if (!cardTemplate) return slotChildren;

        if (dataItems && dataItems.length > 0) {
            return itemDataArray.map((itemData, offset) => (
                <FilterableCardItemDataContext.Provider
                    key={`data-item-${offset}`}
                    value={itemData}
                >
                    <div css={fullHeightCardStyle}>
                        {cloneElement(cardTemplate, {
                            key: `data-card-${offset}`
                        })}
                    </div>
                </FilterableCardItemDataContext.Provider>
            ));
        }

        const backfilledItems = itemDataArray.slice(registeredItems.length);
        if (backfilledItems.length === 0) return slotChildren;
        const backfillItems = backfilledItems.map((data, offset) => (
            <FilterableCardItemDataContext.Provider
                key={`backfill-${offset}`}
                value={data}
            >
                <div css={fullHeightCardStyle}>
                    {cloneElement(cardTemplate, {
                        key: `backfill-card-${offset}`
                    })}
                </div>
            </FilterableCardItemDataContext.Provider>
        ));
        return [...slotChildren, ...backfillItems];
    }, [items, dataItems, itemDataArray]);

    const isDataDrivenMode = dataItems !== undefined;

    const orderedItems = useMemo(() => {
        const slotItems = allSlotItems;
        if (!isRegistered) {
            if (isDataDrivenMode) return [];
            return slotItems.map((item, index) => ({
                index,
                item,
                visible: true
            }));
        }

        const visible = visibleIndices.map((index) => ({
            index,
            item: slotItems[index],
            visible: true
        }));
        const hidden = slotItems
            .map((item, index) => ({ index, item, visible: false }))
            .filter(({ index }) => !visibleIndexSet.has(index));

        return [...visible, ...hidden];
    }, [
        isRegistered,
        isDataDrivenMode,
        allSlotItems,
        visibleIndices,
        visibleIndexSet
    ]);

    const shouldRenderCards = !isAwaitingBackfill;

    const renderedItems = useMemo(
        () => (shouldRenderCards ? orderedItems : []),
        [orderedItems, shouldRenderCards]
    );

    const measurementItems = useMemo(
        () => orderedItems.filter(({ visible }) => visible),
        [orderedItems]
    );
    const {
        measuredSkeletonHeight,
        isSkeletonMeasurementReady,
        measurementItemRefs
    } = useMeasuredSkeletonHeight({ shouldRenderCards, measurementItems });
    const animatedVisibleIndices = useAnimatedVisibleIndices(
        shouldRenderCards,
        visibleIndices
    );

    const filteredItems = useMemo(
        () =>
            renderedItems
                .filter(({ visible }) => visible)
                .map(({ item }) => item),
        [renderedItems]
    );

    const handleLoadMoreClick = useCallback(() => {
        setVisibleCount((previousVisibleCount) =>
            getNextVisibleCount(previousVisibleCount, pageSize)
        );
    }, [pageSize]);

    return (
        <>
            <ColumnLayout layout="12">
                <Toolbar
                    filterDefs={filterDefs}
                    sortDefs={sortDefs}
                    searchFieldPaths={searchFieldPaths}
                    showSearchInput={shouldShowSearchInput}
                    filterObject={filterObject}
                    searchQuery={searchQuery}
                    selectedSortId={selectedSortId}
                    dropdownVisibility={dropdownVisibility}
                    labelMaps={labelMaps}
                    onSearchChange={setSearchQuery}
                    onSearchSubmit={handleSearchSubmitAndShowAll}
                    onSearchClear={handleSearchClear}
                    onFilterButtonClick={handleFilterButtonClick}
                    onOptionToggle={toggleFilterOption}
                    onClearAllFilters={clearAllFilters}
                    onSortChange={handleSortChange}
                />
            </ColumnLayout>
            <FilterableGalleryProvider
                items={items}
                filteredItems={filteredItems}
            >
                <div css={gallerySlotStyle}>
                    {isRegistered &&
                    sortedMatchingDataItems.length === 0 &&
                    itemDataArray.length > 0 ? (
                        <p
                            css={emptyStateStyle}
                            data-testid="filterable-gallery-empty"
                        >
                            {emptyStateLabel}
                        </p>
                    ) : null}
                    <ColumnLayout layout={layout} renderAsList="ul">
                        {!shouldRenderCards && isSkeletonMeasurementReady ? (
                            <FilterableGallerySkeletonCards
                                count={skeletonCardCount}
                                height={measuredSkeletonHeight}
                            />
                        ) : null}
                        {renderedItems.map(
                            ({ index, item, visible }, renderIndex) => (
                                <FilterableItemIndexContext.Provider
                                    key={index}
                                    value={index}
                                >
                                    <li
                                        hidden={visible ? undefined : true}
                                        css={[
                                            galleryItemStyle,
                                            visible
                                                ? galleryItemEntranceStyle
                                                : undefined,
                                            visible &&
                                            animatedVisibleIndices.has(index)
                                                ? galleryItemVisibleStyle
                                                : undefined
                                        ]}
                                        style={
                                            visible
                                                ? getCardEntranceStyle(
                                                      getCardEntranceDelayIndex(
                                                          renderIndex
                                                      )
                                                  )
                                                : undefined
                                        }
                                        data-testid={
                                            visible
                                                ? "filterable-gallery-item"
                                                : undefined
                                        }
                                    >
                                        {item}
                                    </li>
                                </FilterableItemIndexContext.Provider>
                            )
                        )}
                    </ColumnLayout>
                    {!shouldRenderCards ? (
                        <MeasuredGalleryItems
                            items={measurementItems}
                            refs={measurementItemRefs}
                            layout={layout}
                        />
                    ) : null}
                    {hasMoreToShow && (
                        <div css={loadMoreStyle}>
                            <button
                                type="button"
                                css={loadMoreButtonStyle}
                                data-testid="load-more-button"
                                onClick={handleLoadMoreClick}
                            >
                                <span css={loadMoreChevronStyle}>
                                    <ChevronRightIcon
                                        label=""
                                        spacing="spacious"
                                    />
                                </span>
                                <TypographyStyles>
                                    <p
                                        className="body-sm"
                                        css={loadMoreLabelStyle}
                                    >
                                        {loadMoreLabel ?? "Load more"}
                                    </p>
                                </TypographyStyles>
                            </button>
                        </div>
                    )}
                </div>
            </FilterableGalleryProvider>
        </>
    );
};

const UniformFilterableGallery = ({
    searchFieldPaths: rawSearchFieldPaths,
    showSearchInput = true,
    displayLimit,
    filters: rawFilters,
    sorts: rawSorts,
    urlSync = true,
    layout = "4-4-4",
    facetDataSources: rawFacetDataSources,
    pageSize: rawPageSize,
    data,
    featuredItemSlugs: rawFeaturedItemSlugs,
    loadMoreLabel,
    emptyStateLabel
}: UniformFilterableGalleryProps): JSX.Element => {
    const intl = useIntl();
    // CMS-provided label wins (localized on the Uniform side); otherwise fall
    // back to the localized default for the active page locale.
    const resolvedEmptyStateLabel =
        emptyStateLabel ?? intl.formatMessage(messages.emptyState);

    const itemDataArrayRef = useRef<unknown[]>([]);
    const [, setDataVersion] = useState(0);

    const insideContainer = useIsInsideLoaderProvider();

    const pageSize = resolvePositiveIntegerParameter(rawPageSize);
    const featuredItemSlugs = useMemo(
        () => resolveFeaturedSlugs(rawFeaturedItemSlugs),
        [rawFeaturedItemSlugs]
    );

    const handleRegistryChange = useCallback((data: unknown[]) => {
        const previousData = itemDataArrayRef.current;
        if (
            data.length !== previousData.length ||
            data.some((item, index) => item !== previousData[index])
        ) {
            itemDataArrayRef.current = data;
            setDataVersion((v) => v + 1);
        }
    }, []);

    const wrapperComponent = useCallback(
        (slotProps: UniformSlotWrapperComponentProps) => (
            <FilterableGallerySlot
                {...slotProps}
                itemDataArrayRef={itemDataArrayRef}
                rawSearchFieldPaths={rawSearchFieldPaths}
                showSearchInput={showSearchInput}
                displayLimit={displayLimit}
                rawFilters={rawFilters}
                rawSorts={rawSorts}
                urlSync={urlSync}
                layout={layout}
                rawFacetDataSources={rawFacetDataSources}
                pageSize={pageSize}
                dataItems={toDataItems(data)}
                featuredItemSlugs={featuredItemSlugs}
                loadMoreLabel={loadMoreLabel}
                emptyStateLabel={resolvedEmptyStateLabel}
            />
        ),
        [
            rawSearchFieldPaths,
            showSearchInput,
            displayLimit,
            rawFilters,
            rawSorts,
            urlSync,
            layout,
            rawFacetDataSources,
            pageSize,
            data,
            featuredItemSlugs,
            loadMoreLabel,
            resolvedEmptyStateLabel
        ]
    );

    const galleryContent = (
        <FilterableItemRegistryProvider onRegistryChange={handleRegistryChange}>
            <UniformSlot
                name="filterableItems"
                wrapperComponent={wrapperComponent}
            />
        </FilterableItemRegistryProvider>
    );

    return (
        <UFOSegment name="uniform-filters">
            <TypographyStyles>
                {insideContainer ? (
                    galleryContent
                ) : (
                    <FilterableGalleryLoaderProvider>
                        {galleryContent}
                    </FilterableGalleryLoaderProvider>
                )}
            </TypographyStyles>
        </UFOSegment>
    );
};

const resolveBooleanParameter = (
    parameter: boolean | { value?: boolean } | undefined,
    defaultValue: boolean
) => {
    if (typeof parameter === "boolean") return parameter;
    if (typeof parameter?.value === "boolean") return parameter.value;
    return defaultValue;
};

const resolvePositiveIntegerParameter = (
    parameter: number | string | { value?: number | string } | undefined
) => {
    const rawValue =
        typeof parameter === "object" && parameter !== null
            ? parameter.value
            : parameter;
    const value = typeof rawValue === "string" ? Number(rawValue) : rawValue;

    if (typeof value !== "number" || !Number.isFinite(value)) return undefined;

    const integerValue = Math.floor(value);
    return integerValue > 0 ? integerValue : undefined;
};

const UNRANKED_PRIORITY = Number.MAX_SAFE_INTEGER;

const resolveFeaturedSlugs = (
    parameter: UniformValueParameter<string[]> | undefined
): string[] => {
    const rawValue = unwrapParameterValue(parameter);
    if (!Array.isArray(rawValue)) return [];

    return rawValue
        .map(normalizeUrlSlug)
        .filter((slug): slug is string => slug !== undefined);
};

const prioritizeItemsBySlug = (
    items: unknown[],
    featuredItemSlugs: string[]
): unknown[] => {
    if (featuredItemSlugs.length === 0) return items;

    const priorityBySlug = new Map(
        featuredItemSlugs.map((slug, index) => [slug, index])
    );

    return [...items].sort(
        (a, b) =>
            getSlugPriority(a, priorityBySlug) -
            getSlugPriority(b, priorityBySlug)
    );
};

const getSlugPriority = (
    item: unknown,
    priorityBySlug: Map<string, number>
): number => {
    const slug = getItemFullUrlSlug(item);
    if (slug === undefined) return UNRANKED_PRIORITY;
    return priorityBySlug.get(slug) ?? UNRANKED_PRIORITY;
};

const getOrderedIndicesForDataItems = (
    sourceDataItems: unknown[],
    orderedDataItems: unknown[]
) => {
    const indexMap = new Map<unknown, number[]>();
    sourceDataItems.forEach((dataItem, index) => {
        const indices = indexMap.get(dataItem) ?? [];
        indices.push(index);
        indexMap.set(dataItem, indices);
    });

    return orderedDataItems.flatMap((dataItem) => {
        const indices = indexMap.get(dataItem);
        const index = indices?.shift();
        return index === undefined ? [] : [index];
    });
};

const SKELETON_CARD_MIN_HEIGHT = 360;
const CARD_ENTRANCE_START_DELAY_MS = 100;
const CARD_ENTRANCE_DELAY_SECONDS = 0.2;
const MAX_CARD_ENTRANCE_DELAY_SECONDS = 1.2;
const MAX_STAGGERED_CARD_COUNT =
    MAX_CARD_ENTRANCE_DELAY_SECONDS / CARD_ENTRANCE_DELAY_SECONDS + 1;

interface GalleryItemRenderState {
    index: number;
    item: ReactNode;
    visible: boolean;
}

interface MeasuredGalleryItemsProps {
    items: GalleryItemRenderState[];
    refs: MutableRefObject<(HTMLLIElement | null)[]>;
    layout: ColumnLayoutProps["layout"];
}

const MeasuredGalleryItems = ({
    items,
    refs,
    layout
}: MeasuredGalleryItemsProps): JSX.Element => (
    <div css={measurementLayerStyle} aria-hidden="true">
        <ColumnLayout layout={layout} renderAsList="ul">
            {items.map(({ index, item }, offset) => (
                <li
                    key={index}
                    ref={(element) => {
                        refs.current[offset] = element;
                    }}
                    css={galleryItemStyle}
                >
                    {item}
                </li>
            ))}
        </ColumnLayout>
    </div>
);

const FilterableGallerySkeletonCards = ({
    count,
    height
}: {
    count: number;
    height?: number;
}): JSX.Element => (
    <>
        {Array.from({ length: count }, (_, index) => (
            <li
                key={index}
                css={[galleryItemStyle, skeletonCardItemStyle]}
                data-testid="filterable-gallery-skeleton-item"
                aria-hidden="true"
            >
                <div css={fullHeightCardStyle}>
                    <div
                        css={skeletonCardStyle}
                        style={height ? { minHeight: height } : undefined}
                    >
                        <div css={skeletonMediaStyle}>
                            <Adg4Skeleton width="100%" height="100%" />
                        </div>
                        <div css={skeletonContentStyle}>
                            <Adg4Skeleton width="40%" height="16px" />
                            <div css={skeletonTitleGroupStyle}>
                                <Adg4Skeleton
                                    width={getSkeletonLineWidth(index, 0)}
                                    height="24px"
                                />
                                <Adg4Skeleton
                                    width={getSkeletonLineWidth(index, 1)}
                                    height="24px"
                                />
                            </div>
                            <div css={skeletonBodyGroupStyle}>
                                <Adg4Skeleton width="100%" height="16px" />
                                <Adg4Skeleton
                                    width={getSkeletonLineWidth(index, 2)}
                                    height="16px"
                                />
                            </div>
                            <Adg4Skeleton width="32%" height="20px" />
                        </div>
                    </div>
                </div>
            </li>
        ))}
    </>
);

interface UseMeasuredSkeletonHeightArgs {
    shouldRenderCards: boolean;
    measurementItems: GalleryItemRenderState[];
}

const useMeasuredSkeletonHeight = ({
    shouldRenderCards,
    measurementItems
}: UseMeasuredSkeletonHeightArgs): {
    measuredSkeletonHeight: number | undefined;
    isSkeletonMeasurementReady: boolean;
    measurementItemRefs: MutableRefObject<(HTMLLIElement | null)[]>;
} => {
    const [measuredSkeletonHeight, setMeasuredSkeletonHeight] = useState<
        number | undefined
    >();
    const [hasMeasuredSkeletonHeight, setHasMeasuredSkeletonHeight] =
        useState(false);
    const measurementItemRefs = useRef<(HTMLLIElement | null)[]>([]);

    useLayoutEffect(() => {
        if (shouldRenderCards) {
            setMeasuredSkeletonHeight(undefined);
            setHasMeasuredSkeletonHeight(false);
            return;
        }

        const maxHeight = getMaxElementHeight(measurementItemRefs.current);
        setMeasuredSkeletonHeight(
            maxHeight > SKELETON_CARD_MIN_HEIGHT ? maxHeight : undefined
        );
        setHasMeasuredSkeletonHeight(true);
    }, [shouldRenderCards, measurementItems]);

    return {
        measuredSkeletonHeight,
        isSkeletonMeasurementReady:
            shouldRenderCards ||
            measurementItems.length === 0 ||
            hasMeasuredSkeletonHeight,
        measurementItemRefs
    };
};

const useAnimatedVisibleIndices = (
    shouldRenderCards: boolean,
    visibleIndices: number[]
): Set<number> => {
    const [animatedVisibleIndices, setAnimatedVisibleIndices] = useState<
        Set<number>
    >(() => new Set());

    useEffect(() => {
        if (!shouldRenderCards) {
            setAnimatedVisibleIndices(new Set());
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setAnimatedVisibleIndices((previousIndices) => {
                const nextIndices = new Set(previousIndices);
                visibleIndices.forEach((index) => nextIndices.add(index));
                return nextIndices;
            });
        }, CARD_ENTRANCE_START_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
    }, [shouldRenderCards, visibleIndices]);

    return animatedVisibleIndices;
};

const getSkeletonLineWidth = (index: number, offset: number): string => {
    const widths = ["82%", "64%", "92%", "56%", "74%"];
    return widths[(index + offset) % widths.length] ?? "72%";
};

const getCardEntranceStyle = (index: number): CSSProperties => ({
    transitionDelay: `${getCardEntranceDelay(index)}s`
});

const getCardEntranceDelayIndex = (renderIndex: number): number =>
    renderIndex % MAX_STAGGERED_CARD_COUNT;

const getCardEntranceDelay = (index: number): number =>
    Math.min(
        CARD_ENTRANCE_DELAY_SECONDS * index,
        MAX_CARD_ENTRANCE_DELAY_SECONDS
    );

const resolveSkeletonCardCount = (
    displayLimit: number | undefined,
    ssrItemCount: number
): number => displayLimit ?? ssrItemCount;

const getShouldApplyDisplayLimit = (
    displayLimit: number | undefined,
    shouldShowAllSearchResults: boolean
): boolean => typeof displayLimit === "number" && !shouldShowAllSearchResults;

const getUsesVisibleCount = (
    pageSize: number | undefined,
    dataItems: GalleryDataItem[] | undefined
): boolean => pageSize !== undefined || dataItems !== undefined;

const getNextVisibleCount = (
    previousVisibleCount: number,
    pageSize: number | undefined
): number =>
    pageSize !== undefined ? previousVisibleCount + pageSize : Infinity;

const getMaxElementHeight = (elements: (HTMLElement | null)[]): number =>
    elements.reduce(
        (maxHeight, element) =>
            Math.max(maxHeight, element?.getBoundingClientRect().height ?? 0),
        0
    );

const measurementLayerStyle = css({
    height: 0,
    overflow: "hidden",
    visibility: "hidden"
});

const gallerySlotStyle = css({
    marginBlockStart: token("space.400"),
    // ColumnLayout owns the <ul>; this is the only hook for equal grid rows.
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ul: {
        gridAutoRows: "1fr"
    }
});

const emptyStateStyle = css({
    padding: token("space.200"),
    color: token("color.text.subtlest"),
    fontStyle: "italic",
    textAlign: "center"
});

const galleryItemStyle = css({
    "&:not([hidden])": {
        display: "grid",
        height: "100%"
    },
    "&[hidden]": {
        display: "none"
    }
});

const galleryItemEntranceStyle = css({
    "opacity": 0,
    "transform": "translate3d(0, 60px, 0)",
    "transition": "opacity .3s ease-in-out, transform .3s ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
        opacity: 1,
        transform: "translate3d(0, 0, 0)",
        transition: "none"
    }
});

const galleryItemVisibleStyle = css({
    opacity: 1,
    transform: "translate3d(0, 0, 0)"
});

const fullHeightCardStyle = css({
    height: "100%",
    display: "grid"
});

const skeletonCardItemStyle = css({
    listStyleType: "none"
});

const skeletonCardStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: `${SKELETON_CARD_MIN_HEIGHT}px`,
    overflow: "hidden",
    backgroundColor: token("elevation.surface", "#FFFFFF"),
    borderRadius: wacToken("corner.radius.card"),
    boxShadow: token("elevation.shadow.raised")
});

const skeletonMediaStyle = css({
    height: "168px",
    overflow: "hidden"
});

const skeletonContentStyle = css({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: token("space.200"),
    padding: token("space.300")
});

const skeletonTitleGroupStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100")
});

const skeletonBodyGroupStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.075"),
    marginBlockEnd: "auto"
});

const loadMoreStyle = css({
    display: "flex",
    justifyContent: "center",
    paddingBlock: token("space.400")
});

const loadMoreButtonStyle = css({
    appearance: "none",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: token("space.050")
});

const loadMoreChevronStyle = css({
    height: "24px",
    width: "24px",
    display: "inline-flex",
    alignItems: "center"
});

const loadMoreLabelStyle = css({
    margin: 0,
    marginBlockStart: "1px",
    textAlign: "left"
});

function resolveInitialVisibleCount(
    pageSize: number | undefined,
    dataItemsLength: number | undefined
): number {
    if (pageSize !== undefined) return pageSize;
    if (dataItemsLength !== undefined) return dataItemsLength;
    return Infinity;
}

function toDataItems(data: unknown): GalleryDataItem[] | undefined {
    return Array.isArray(data) && data.length > 0 ? data : undefined;
}

function getUniformBlockEntries(data: unknown): FacetDataSourceEntry[] {
    const entries = Array.isArray(data)
        ? data
        : isRecord(data) && Array.isArray(data["value"])
        ? data["value"]
        : [];

    return entries.filter(isFacetDataSourceEntry);
}

function isFacetDataSourceEntry(entry: unknown): entry is FacetDataSourceEntry {
    return isRecord(entry);
}

function getSysId(item: unknown): string | undefined {
    if (!isRecord(item)) return undefined;
    const sys = item["sys"];
    if (!isRecord(sys)) return undefined;
    return typeof sys["id"] === "string" ? sys["id"] : undefined;
}

function getItemFullUrlSlug(item: unknown): string | undefined {
    if (!isRecord(item)) return undefined;
    const fields = item["fields"];
    if (!isRecord(fields)) return undefined;
    return normalizeUrlSlug(fields["fullUrlSlug"]);
}

function normalizeUrlSlug(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;

    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) return undefined;

    const pathname = extractPathname(trimmedValue);
    return stripTrailingSlash(stripQueryAndFragment(pathname));
}

function extractPathname(value: string): string {
    const valueWithProtocol = value.startsWith("//") ? `https:${value}` : value;
    try {
        return new URL(valueWithProtocol).pathname;
    } catch {
        return value.startsWith("/") ? value : `/${value}`;
    }
}

function stripQueryAndFragment(path: string): string {
    return path.split(/[?#]/, 1)[0] ?? path;
}

function stripTrailingSlash(path: string): string {
    if (path === "/") return path;
    return path.endsWith("/") ? path.slice(0, -1) : path;
}

function unwrapParameterValue<T>(
    parameter: UniformValueParameter<T> | undefined
): T | undefined {
    if (parameter === undefined) return undefined;
    if (typeof parameter !== "object" || parameter === null)
        return parameter as T;
    if (Array.isArray(parameter)) return parameter as T;
    return (parameter as { value?: T }).value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function useFilterableBackfillState(): {
    extraItems: unknown[];
    isAwaitingBackfill: boolean;
} {
    const loaderContext = useFilterableGalleryLoader();
    const extraItems = loaderContext?.extraItems ?? [];
    const hasLoaderRegistered =
        loaderContext?.currentRegistration !== null &&
        loaderContext?.currentRegistration !== undefined;
    const isAwaitingBackfill = hasLoaderRegistered && extraItems.length === 0;
    return { extraItems, isAwaitingBackfill };
}

export default UniformFilterableGallery;
